import axios from "axios";
import {
  normalizeBackendTabs,
  normalizeRoleName,
  normalizeTabName,
  normalizeTabIds,
} from "../utils/access";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/role`;
const TABS_API_BASE_URL = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/tabs`;

const extractRoles = (response) => {
  const data = response?.data?.data || response?.data || [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.roles)) return data.roles;
  if (Array.isArray(data.items)) return data.items;
  return data && typeof data === "object" ? [data] : [];
};

const findRoleByName = (roles, roleName) => {
  const normalizedRoleName = normalizeRoleName(roleName);
  return roles.find(
    (item) => normalizeRoleName(item?.role || item?.roleName || item?.role_name) === normalizedRoleName
  );
};

const toRoleAccess = (role) => ({
  role,
  tabIds: normalizeTabIds(role?.tab_id || role?.tabId || role?.tabIds),
});

const isActiveRecord = (item) => {
  if (item?.is_active === undefined) return true;
  const activeValue = String(item.is_active).trim().toLowerCase();
  return activeValue === "1" || activeValue === "true";
};

const IGNORED_BACKEND_TAB_NAMES = [
  "Update User",
  "Employees",
  "Login Attempts",
  "View Profile",
  "Experiment Report",
  "Battle Report",
  "War Report",
  "Skill Report",
  "Skills Report",
].map(normalizeTabName);

const isStandalonePermissionTab = (tab) =>
  !IGNORED_BACKEND_TAB_NAMES.includes(normalizeTabName(tab?.tab_name));

const roleService = {
  getTabs: async (token) => {
    try {
      const response = await axios.get(`${TABS_API_BASE_URL}/all`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const tabs = normalizeBackendTabs(response?.data?.data || response?.data || []);
      return tabs.filter(isActiveRecord).filter(isStandalonePermissionTab);
    } catch (error) {
      console.error("Error fetching tabs:", error.response?.data || error);
      throw error;
    }
  },

  getRoles: async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/all`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return extractRoles(response).filter(isActiveRecord);
    } catch (error) {
      console.error("Error fetching roles:", error.response?.data || error);
      throw error;
    }
  },

  createRole: async (token, newRole) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/add`, {
        ...newRole,
        tabIds: normalizeTabIds(newRole?.tabIds),
      }, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating role:", error.response?.data || error);
      throw error;
    }
  },

  updateRole : async (token, roleId, roleUpdate) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/update/${roleId}`, {
        role: roleUpdate?.role,
        tabIds: normalizeTabIds(roleUpdate?.tabIds),
      } , {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response)
      return response.data;      
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  deleteRole: async (token, roleId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/delete/${roleId}`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting role:", error.response?.data || error);
      throw error;
    }
  },
  
  getUsersByRole: async (token, roleName) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/all/${roleName}`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching users by role:", error.response?.data || error);
      throw error;
    }
  },

  getRoleAccess: async (token, roleName) => {
    if (!roleName) return toRoleAccess(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/all/${encodeURIComponent(roleName)}`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const roles = extractRoles(response);
      return toRoleAccess(findRoleByName(roles, roleName) || roles[0]);
    } catch (error) {
      console.error("Error fetching role access by role:", error.response?.data || error);
    }

    const roles = await roleService.getRoles(token);
    return toRoleAccess(findRoleByName(roles, roleName));
  },

};


export default roleService;
